import { SearchPredicate } from '@nx-smartmonkey/shared/domain';
import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { PaginatedResult } from '@nx-smartmonkey/shared/interfaces';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { backofficeService } from '../../application/BackofficeService';
import { User } from '../../domain/backoffice/user/User';
import useErrorMiddleware from '../errorMiddleware/useErrorMiddleware';

import { useShowSnackbarMessage } from '../snackbarMessage/useShowSnackbarMessage';

interface UseSearchUsersProps {
  text?: string;
}

type UseSearchUsersResponse = ServiceResponse<Function, PaginatedResult<User> | undefined>;

// Hooks act as 'Adapter' layer.
export const useSearchUsers = ({ text }: UseSearchUsersProps): UseSearchUsersResponse => {
  const { showSnackbarMessage } = useShowSnackbarMessage();
  const { sessionExpiredLogout } = useErrorMiddleware();
  const [paginatedUsers, setPaginatedUsers] = useState<PaginatedResult<User> | undefined>(undefined);

  const {
    isInitialLoading: isLoading,
    refetch,
    isError,
    isSuccess,
  } = useQuery(
    [`sidebar_search_users`, text],
    () =>
      backofficeService.searchUsers({
        predicates: [
          ...(text && text !== ``
            ? [
                SearchPredicate.create({
                  field: `email`,
                  operator: `contains`,
                  type: `string`,
                  value: text,
                }),
              ]
            : []),
        ],
        offset: 0,
        limit: 20,
      }),
    {
      // enabled: false,
      retry: false,
      refetchOnWindowFocus: false,
      onSuccess: (adminUsersPagination) => {
        setPaginatedUsers(adminUsersPagination);
      },
      onError: (err: any) => {
        console.info(`Planner Administration Retrieve Users Error: `, err?.response);

        if (err?.response?.status === 401) {
          sessionExpiredLogout();
          return;
        }

        showSnackbarMessage({
          messageIntlId: `infoerror.${err?.response?.data?.messageId ?? `network_error`}`,
        });
      },
    }
  );

  return {
    error: isError,
    isLoading,
    success: isSuccess,
    run: refetch,
    data: paginatedUsers,
  };
};
